import { Box, Grid, Typography } from "@mui/material";
import { DateCalendar, DigitalClock } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { handleData } from "../../features/formSlice";
import {
  getNextValidBookingDate,
  isBlockedBookingDate,
  toSaudiDay,
} from "./utils/bookingDateRules";

const Calender = () => {
  const { t, i18n } = useTranslation();
  const today = toSaudiDay(dayjs());
  const minDate = today?.add(24, "hour") ?? null;
  const maxDate = today?.add(30, "day") ?? null;
  const initialCandidate = minDate;
  const initialSelectedDate =
    getNextValidBookingDate(initialCandidate, { maxDate }) ?? null;

  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [selectedTime, setSelectedTime] = useState(initialSelectedDate);
  const [minTime, setMinTime] = useState(null);
  const [isSelectedDateDisabled, setIsSelectedDateDisabled] = useState(
    initialSelectedDate ? isBlockedBookingDate(initialSelectedDate) : true
  );
  const dispatch = useDispatch();

  const handleDateChange = newDate => {
    const normalizedDate = toSaudiDay(newDate);
    const nextValidDate = getNextValidBookingDate(normalizedDate, { maxDate });

    setSelectedDate(nextValidDate);
    setIsSelectedDateDisabled(!nextValidDate);

    if (nextValidDate) {
      dispatch(handleData(nextValidDate.toISOString()));
    }
  };

  useEffect(() => {
    if (!selectedDate || !minDate) {
      setMinTime(null);
      return;
    }

    const minTimeValue = selectedDate.isSame(minDate, "day") ? minDate : null;

    setMinTime(minTimeValue);
  }, [minDate, selectedDate]);

  const handleTimeChange = newTime => {
    if (!newTime) {
      return;
    }

    setSelectedTime(newTime);
    dispatch(handleData(newTime.toISOString()));
  };

  useEffect(() => {
    setSelectedTime(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    setIsSelectedDateDisabled(selectedDate ? isBlockedBookingDate(selectedDate) : true);
  }, [selectedDate]);

  const shouldDisableAM = time => {
    return time.format("a") === "am"; // Disable all AM times
  };

  const shouldDisableDate = date => isBlockedBookingDate(date);

  return (
    <>
      <Box
        spacing={4}
        sx={{
          alignItems: "flex-start",
          justifyContent: "flex-start",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography
          variant="p"
          color={"black"}
          sx={{
            fontWeight: 600,
            fontSize: { md: "16px", sm: "12px", xs: "12px" },
            textAlign: "start",
          }}
        >
          {t(
            "We hope you choose the appointment that suits you, and you will be notified for confirmation."
          )}
        </Typography>
      </Box>
      <Grid
        container
        spacing={4}
        direction={"row"}
        display={"flex"}
        sx={{ alignItems: "flex-start", justifyContent: "flex-start" }}
      >
        <Grid
          item
          md={6}
          display={"flex"}
          sx={{ flexDirection: "column" }}
          sm={12}
          xs={12}
        >
          <Typography
            variant="h5"
            textAlign={"start"}
            mb={1}
            sx={{
              fontWeight: 600,
              fontSize: { md: "16px", sm: "12px", xs: "12px" },
            }}
          >
            {t("Day")}
          </Typography>

          <DateCalendar
            label="Date"
            value={selectedDate}
            sx={{
              color: "black",
              backgroundColor: "#f8f8f8",
              width: "100%",
              direction: "ltr",
            }}
            onChange={newDate => handleDateChange(newDate)}
            minDate={minDate}
            maxDate={maxDate}
            shouldDisableDate={shouldDisableDate}
          />
        </Grid>
        {!isSelectedDateDisabled && (
          <Grid
            item
            md={6}
            display={"flex"}
            sx={{ flexDirection: "column" }}
            sm={12}
            xs={12}
          >
            <Typography
              variant="h5"
              textAlign={"start"}
              mb={1}
              sx={{
                fontWeight: 600,
                fontSize: { md: "16px", sm: "12px", xs: "12px" },
              }}
            >
              {t("Time")}
            </Typography>

            <DigitalClock
              value={selectedTime}
              sx={{
                color: "black",
                direction: i18n.language === "ar" ? "rtl" : "ltr",
                backgroundColor: "#f8f8f8",
                width: "100%",
              }}
              minTime={minTime}
              onChange={newTime => handleTimeChange(newTime)}
              shouldDisableTime={shouldDisableAM}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default Calender;
